import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { serviceSlug } from "../../data/serviceDetails";
import { ArrowRight, Check, RotateCw, Zap } from "lucide-react";
import { serviceImage } from "../hero/productImages";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Front = ({ s }) => (
  <div className="pp-flip-face bg-white border border-slate-200/80 p-6 flex flex-col">
    <div className="relative -mx-6 -mt-6 mb-5 h-44 rounded-t-[28px] overflow-hidden bg-[#050C17]">
      <img src={serviceImage(s.visual)} alt={s.label} className="w-full h-full object-cover object-[center_45%] transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050C17] via-transparent to-transparent" />
      <span className="absolute left-5 bottom-4 text-[10px] font-bold tracking-[0.18em] text-[#67E8F9] bg-[#06B6D4]/15 border border-[#06B6D4]/30 px-2.5 py-1 rounded-full backdrop-blur">
        {s.kicker}
      </span>
    </div>
    <h3 className="text-2xl font-extrabold leading-tight text-[#0D1B2A] tracking-tight">
      {s.title} <span className="pp-grad-text">{s.title_accent}</span> {s.title_tail}
    </h3>
    <p className="mt-3 text-sm text-[#475569] leading-relaxed line-clamp-3">{s.desc}</p>
    <div className="mt-auto pt-5 flex items-center justify-between">
      <div className="flex flex-wrap gap-1.5">
        {(s.highlights || []).slice(0, 2).map((h) => (
          <span key={h.title} className="text-[11px] font-semibold text-[#0EA5E9] bg-[#F0FDFA] px-2.5 py-1 rounded-full">{h.title}</span>
        ))}
      </div>
      <span className="flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] shrink-0"><RotateCw size={12} /> Flip for details</span>
    </div>
  </div>
);

const Back = ({ s }) => {
  const navigate = useNavigate();
  return (
  <div className="pp-flip-face pp-flip-back bg-[#050C17] text-white p-6 flex flex-col border border-[#06B6D4]/30">
    <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#06B6D4]/25 blur-3xl" />
    <div className="relative text-[10px] font-bold tracking-[0.18em] text-[#10B981]">{s.label}</div>
    <h4 className="relative mt-2 text-xl font-extrabold tracking-tight">{s.title} <span className="text-[#06B6D4]">{s.title_accent}</span></h4>
    <ul className="relative mt-4 space-y-3">
      {[...(s.highlights || []), ...(s.chips || [])].slice(0, 5).map((h) => (
        <li key={h.title} className="flex gap-3">
          <span className="w-5 h-5 mt-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] flex items-center justify-center shrink-0"><Check size={12} /></span>
          <div><div className="text-sm font-semibold">{h.title}</div><div className="text-xs text-slate-400">{h.sub}</div></div>
        </li>
      ))}
    </ul>
    <button
      onClick={(e) => { e.stopPropagation(); navigate(`/services/${serviceSlug(s)}`); }}
      data-testid={`service-cta-${slug(s.label)}`}
      className="relative mt-auto group inline-flex items-center justify-between gap-3 text-[#030712] font-semibold pl-5 pr-2 py-2 rounded-full"
      style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}
    >
      {s.cta || "Get Started"}
      <span className="w-8 h-8 rounded-full bg-[#030712] text-white flex items-center justify-center group-hover:translate-x-1 transition-transform"><ArrowRight size={14} /></span>
    </button>
  </div>
  );
};

export const FlipCard = ({ s, index }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className="pp-flip group pp-flip-enter"
      style={{ animationDelay: `${index * 90}ms` }}
      onClick={() => setFlipped((f) => !f)}
      onMouseLeave={() => setFlipped(false)}
      data-testid={`service-card-${slug(s.label)}`}
      data-flipped={flipped}
    >
      <div className={`pp-flip-inner ${flipped ? "is-flipped" : ""}`}>
        <Front s={s} />
        <Back s={s} />
      </div>
    </div>
  );
};

export const SectionBadge = () => (
  <span className="inline-flex items-center gap-2 text-[#06B6D4] font-semibold uppercase tracking-wider text-sm">
    <Zap size={16} className="fill-[#06B6D4]" /> Services
  </span>
);
